import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Grid,
  Typography,
  Stack,
  FormControl,
  FormHelperText,
  TextField,
  Paper,
  Select,
  MenuItem,
  Autocomplete,
} from '@mui/material';
import { useFormik } from 'formik';
import '../Styles/CategoryStyles.css';
import * as Yup from 'yup';
import { categoryStyles } from '../Styles/CategoryFormStyles';
import {
  useItemAddCategory,
  useEditItemCategory,
} from '../Query/ItemCategoryQuery';
import {
  CreateItemCategoryRequest,
  CreateItemCategoryRequestStoreTypeEnum,
} from '../../client-sdk';
import { primary } from '../../theme';
import { useAllMerchant } from '../Query/MerchantQuery';
import { useAllStores } from '../Query/ManageStoreQuery';
import { useManageCmsParams } from './ManageCMS/useManageCmsParams';

const ItemCategoryForm = ({
  mode,
  onCancel,
  initialData,
}: {
  mode: 'add' | 'edit';
  onCancel: () => void;
  initialData?: {
    id?: string;
    englishName: string;
    arabicName: string;
    addedBy: string;
    storeType?: string;
    storeId?: string;
  };
}) => {
  const { mutate } = useItemAddCategory();
  const { mutate: editItemCategory } = useEditItemCategory();
  const [loading, setLoading] = useState(false);

  console.log('initialData', initialData);

  const [storeType, setStoreType] = useState(initialData ? initialData.storeType : '');
  // const [setMerchantId] = useState('');
  // const [ setStoreId] = useState('');
  const [merchantOptions, setMerchantOptions] = useState<{ label: string; value: string }[]>([]);
  const [storeOptions, setStoreOptions] = useState<{ label: string; value: string }[]>([]);

  const { data: merchantData, refetch: refetchMerchants } = useAllMerchant({ initialData: { storeType } });
  // const { data: storesData, isLoading: isStoresLoading } = useAllStores({ merchantId, storeType });
  const { params, dispatch } = useManageCmsParams();
  const isReady = Boolean(params && Object.keys(params).length > 0);
  const memoizedParams = useMemo(() => {
    return isReady ? params : {};
  }, [isReady, params]);
  const {
    data: storesData, isLoading: isStoresLoading
  } = useAllStores(memoizedParams);

  React.useEffect(() => {
    if (merchantData) {
      setMerchantOptions(
        merchantData.map((item: any) => ({ value: item.id, label: item.name }))
      );
    } else {
      setMerchantOptions([]);
    }
  }, [merchantData]);

  React.useEffect(() => {
    if (storesData && Array.isArray(storesData.data)) {
      setStoreOptions(
        storesData.data.map((store: any) => ({ value: store.id, label: store.nameEnglish }))
      );
    } else {
      setStoreOptions([]);
    }
  }, [storesData]);

  console.log('initialData123', initialData);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      storeType: '',
      merchantId: '',
      storeId: '',
      englishName: initialData?.englishName || '',
      arabicName: initialData?.arabicName || '',
    },
    validationSchema: Yup.object().shape({
      storeType: initialData ? Yup.string().optional() : Yup.string().required('Please select Store Type'),
      merchantId: initialData ? Yup.string().optional() : Yup.string().required('Please select Merchant'),
      storeId: initialData ? Yup.string().optional() : Yup.string().required('Please select Store'),
      englishName: Yup.string()
        .required('Please enter Category Name')
        .max(100, 'Category Name cannot exceed 100 characters')
        .matches(
          /^[A-Za-z][A-Za-z0-9\s!@#$%^&*(),.?":{}|<>_-]*$/,
          'Category Name must start with a letter and can contain letters, numbers, spaces, and special characters'
        )
        .test(
          'not-only-numbers',
          'Category Name cannot be only numbers',
          (value) => {
            if (!value) return false;
            return !/^\d+$/.test(value.trim());
          }
        )
        .test(
          'not-only-spaces',
          'Category Name cannot be only spaces',
          (value) => {
            if (!value) return false;
            return value.trim().length > 0;
          }
        ),
      arabicName: Yup.string()
        .required('Please enter Category Name in Arabic')
        .max(50, 'Category Name cannot exceed 50 characters')
        .matches(
          /^[\u0600-\u06FF][\u0600-\u06FF\s]*$/,
          'Name must start with an Arabic letter and contain only Arabic letters and spaces'
        )
        .test(
          'not-only-spaces',
          'Name cannot be only spaces',
          (value) => {
            if (typeof value !== 'string') return false;
            return value.trim().length > 0;
          }
        ),
    }),
    onSubmit: async (values) => {
      try {
        setLoading(true);
        const itemCategoryRequest: CreateItemCategoryRequest = {
          addedBy: 'Admin',
          nameEnglish: values.englishName,
          nameArabic: values.arabicName,
          storeType: values.storeType as CreateItemCategoryRequestStoreTypeEnum,
          storeId: values.storeId,
        };
        const itemEditCategory: CreateItemCategoryRequest = {
          addedBy: 'Admin',
          nameEnglish: values.englishName,
          nameArabic: values.arabicName,
          storeType: (initialData?.storeType ?? '') as CreateItemCategoryRequestStoreTypeEnum,
          storeId: (initialData?.storeId ?? ''),
        };
        if (mode === 'edit' && initialData?.id) {
          editItemCategory(
            { id: initialData.id, data: itemEditCategory },
            {
              onSuccess: () => {
                formik.resetForm();
                setLoading(false);
                onCancel();
              },
              onError: (error: any) => {
                console.error('Edit Error:', error);
                setLoading(false);
              },
            }
          );
        } else {
          mutate(itemCategoryRequest, {
            onSuccess: () => {
              formik.resetForm();
              setLoading(false);
              onCancel();
            },
            onError: () => {
              setLoading(false);
            },
          });
        }
      } catch (error) {
        console.error('Error during submission:', error);
        setLoading(false);
      }
    },
  });

  const handleNameChange =
    (field: 'englishName' | 'arabicName') =>
      (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        if (value.length > 50) {
          formik.setFieldError(field, 'Name must not exceed 50 characters');
        } else {
          formik.setFieldValue(field, value);
          formik.setFieldError(field, undefined);
        }
      };

  return (
    <Paper
      elevation={1}
      sx={{
        p: 4,
        mt: 2,
        bgcolor: '#fff',
        borderRadius: 1,
        border: '1px solid #ddd',
      }}
    >
      {/* {loading && (
        <Box sx={categoryStyles.loaderBox}>
          <CircularProgress />
        </Box>
      )} */}

      <Box component="form" noValidate onSubmit={formik.handleSubmit}>
        <Grid
          container
          direction="column"
          spacing={3}
          maxWidth="1200px"
          sx={{ width: '100%' }}
        >
          {/* Store Type */}
          {!initialData && (
            <Grid>
              <Box sx={categoryStyles.labelBox}>
                <Typography sx={categoryStyles.labelText}>Select Store Type*</Typography>
                <FormControl fullWidth error={formik.touched.storeType && Boolean(formik.errors.storeType)}>
                  <Select
                    name="storeType"
                    value={formik.values.storeType}
                    displayEmpty
                    onChange={async (e) => {
                      const value = e.target.value;
                      if (value) {
                        formik.setFieldError('storeType', undefined);
                        formik.setFieldError('merchantId', undefined);
                        formik.setFieldError('storeId', undefined);
                        formik.setFieldError('choiceDetails', undefined);
                      }
                      formik.setFieldValue('storeType', value);
                      formik.setFieldTouched('storeType', true, false);
                      setStoreType(value);
                      formik.setFieldValue('merchantId', '');
                      // setMerchantId('');
                      formik.setFieldValue('storeId', '');
                      // setStoreId('');
                      await refetchMerchants();
                      setTimeout(() => {
                        formik.setFieldTouched('storeType', true, false);
                        if (value) {
                          formik.validateField('storeType');
                        }
                      }, 100);
                    }}
                    sx={{ minWidth: 400 }}
                  >
                    <MenuItem value="" disabled>
                      <span style={{ color: 'rgba(0, 0, 0, 0.6)' }}>-- Select --</span>
                    </MenuItem>
                    <MenuItem value="Restaurant">Restaurant</MenuItem>
                    <MenuItem value="GroceryStore">Grocery Store</MenuItem>
                    <MenuItem value="PharmacyStore">Pharmacy Store</MenuItem>
                  </Select>
                  {formik.touched.storeType && formik.errors.storeType && (
                    <FormHelperText error sx={categoryStyles.errorText}>
                      {formik.errors.storeType}
                    </FormHelperText>
                  )}
                </FormControl>
              </Box>
            </Grid>
          )}
          {/* Merchant */}
          {!initialData && (
            <Grid>
              <Box sx={categoryStyles.labelBox}>
                <Typography sx={categoryStyles.labelText}>Select Merchant*</Typography>
                <FormControl fullWidth error={formik.touched.merchantId && Boolean(formik.errors.merchantId)}>
                  <Autocomplete
                    options={merchantOptions}
                    getOptionLabel={(option) => option.label || ''}
                    value={merchantOptions.find((opt) => opt.value === formik.values.merchantId) || null}
                    onChange={(_, newValue) => {
                      const value = newValue ? newValue.value : '';
                      formik.setFieldValue('merchantId', value);
                      formik.setFieldTouched('merchantId', true, false);

                      if (value) {
                        formik.setFieldError('merchantId', undefined);
                        formik.setFieldError('storeId', undefined);
                        formik.setFieldError('choiceDetails', undefined);
                      }

                      // setMerchantId(value);
                      formik.setFieldValue('storeId', '');

                      // setStoreId('');
                      dispatch({ type: 'SET_MERCHANT_ID', payload: value as string });
                      setTimeout(() => {
                        formik.setFieldTouched('merchantId', true, false);
                        if (value) {
                          formik.validateField('merchantId');
                        }
                      }, 100);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder={!formik.values.storeType ? "-- Select Store First --" : "Select Merchant"}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            width: '400px',
                            backgroundColor: !formik.values.storeType ? '#f5f5f5' : '#fff',
                            '& fieldset': {
                              borderColor: !formik.values.storeType ? '#e0e0e0' :
                                (formik.touched.merchantId && formik.errors.merchantId ? '#d32f2f' : '#9e9e9e'),
                            },
                            '&:hover fieldset': {
                              borderColor: !formik.values.storeType ? '#e0e0e0' :
                                (formik.touched.merchantId && formik.errors.merchantId ? '#d32f2f' : '#9e9e9e'),
                            },
                          },
                          '& .MuiInputBase-input': {
                            color: !formik.values.storeType ? 'rgba(0, 0, 0, 0.38)' : 'inherit',
                          },
                          '& .MuiInputBase-root': {
                            width: '400px', // 👈 you can also add here for extra safety
                          },
                        }}
                        disabled={!formik.values.storeType}
                      />
                    )}
                    sx={{
                      minWidth: 400,
                      width: 400,
                    }}
                    disabled={!formik.values.storeType}
                  />
                  {formik.touched.merchantId && formik.errors.merchantId && (
                    <FormHelperText error sx={categoryStyles.errorText}>
                      {formik.errors.merchantId}
                    </FormHelperText>
                  )}
                </FormControl>
              </Box>
            </Grid>
          )}
          {/* Store */}
          {!initialData && (
            <Grid>
              <Box sx={categoryStyles.labelBox}>
                <Typography sx={categoryStyles.labelText}>Select Store*</Typography>
                <FormControl fullWidth error={formik.touched.storeId && Boolean(formik.errors.storeId)}>
                  <Autocomplete
                    options={storeOptions}
                    getOptionLabel={(option) => option.label || ''}
                    value={storeOptions.find((opt) => opt.value === formik.values.storeId) || null}
                    onChange={(_, newValue) => {
                      const value = newValue ? newValue.value : '';
                      formik.setFieldValue('storeId', value);
                      formik.setFieldTouched('storeId', true, false);
                      // setStoreId(value);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder={!formik.values.merchantId ? "-- Select Merchant First --" : "Select Store"}
                        sx={{
                          width: 400,
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: !formik.values.merchantId ? '#f5f5f5' : '#fff',
                            '& fieldset': {
                              borderColor: !formik.values.merchantId ? '#e0e0e0' :
                                (formik.touched.storeId && formik.errors.storeId ? '#d32f2f' : '#9e9e9e'),
                            },
                            '&:hover fieldset': {
                              borderColor: !formik.values.merchantId ? '#e0e0e0' :
                                (formik.touched.storeId && formik.errors.storeId ? '#d32f2f' : '#9e9e9e'),
                            },
                          },
                          '& .MuiInputBase-input': {
                            color: !formik.values.merchantId ? 'rgba(0, 0, 0, 0.38)' : 'inherit',
                          },
                          '& .MuiInputBase-root': {
                            width: '400px', // 👈 you can also add here for extra safety
                          },
                        }}
                        disabled={!formik.values.merchantId}
                      />
                    )}
                    sx={{
                      minWidth: 400,
                      width: 400,
                    }}
                    disabled={!formik.values.merchantId}
                    loading={isStoresLoading}
                  />
                  {formik.touched.storeId && formik.errors.storeId && (
                    <FormHelperText error sx={categoryStyles.errorText}>
                      {formik.errors.storeId}
                    </FormHelperText>
                  )}
                </FormControl>
              </Box>
            </Grid>
          )}
          {/* English Name */}
          <Grid>
            <Box sx={categoryStyles.labelBox}>
              <Typography sx={categoryStyles.labelText}>
                Category Name (In English)*
              </Typography>
              <FormControl
                fullWidth
                error={
                  formik.touched.englishName &&
                  Boolean(formik.errors.englishName)
                }
                sx={{
                  alignItems: 'flex-start',
                  '& .MuiInput-root': {
                    backgroundColor: '#fff',
                    borderRadius: '4px',
                    padding: '8px 12px',
                    '&:hover:not(.Mui-disabled):before': {
                      borderBottom: '2px solid rgba(0, 0, 0, 0.42)',
                    },
                    '&:before': {
                      borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                    },
                  },
                }}
              >
                <TextField
                  placeholder="Enter Category Name"
                  name="englishName"
                  value={formik.values.englishName}
                  onChange={handleNameChange('englishName')}
                  onBlur={formik.handleBlur}
                  sx={{
                    '& .MuiInputBase-root': {
                      fontSize: 14,
                      color: 'text.primary',
                      width: '400px',
                      height: '45px',
                      marginBottom: '8px', // Added margin bottom for spacing
                    },
                    '& .MuiFormLabel-root': {
                      fontSize: 14,
                    },
                  }}
                />
                {formik.touched.englishName && formik.errors.englishName && (
                  <FormHelperText error sx={categoryStyles.errorText }>
                    {formik.errors.englishName}
                  </FormHelperText>
                )}
              </FormControl>
            </Box>
          </Grid>

          {/* Arabic Name */}
          <Grid>
            <Box sx={categoryStyles.labelBox}>
              <Typography sx={categoryStyles.labelText}>
                Category Name (In Arabic)*
              </Typography>
              <FormControl
                fullWidth
                error={
                  formik.touched.arabicName && Boolean(formik.errors.arabicName)
                }
                sx={{
                  alignItems: 'flex-start',
                  '& .MuiInput-root': {
                    backgroundColor: '#fff',
                    borderRadius: '4px',
                    padding: '8px 12px',
                    '&:hover:not(.Mui-disabled):before': {
                      borderBottom: '2px solid rgba(0, 0, 0, 0.42)',
                    },
                    '&:before': {
                      borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                    },
                  },
                }}
              >
                <TextField
                  placeholder="Enter Category Name"
                  name="arabicName"
                  value={formik.values.arabicName}
                  onChange={handleNameChange('arabicName')}
                  onBlur={formik.handleBlur}
                  sx={{
                    '& .MuiInputBase-root': {
                      fontSize: 14,
                      color: 'text.primary',
                      width: '400px',
                      height: '45px',
                      marginBottom: '8px', 
                    },
                    '& .MuiFormLabel-root': {
                      fontSize: 14,
                    },
                  }}
                />
                {formik.touched.arabicName && formik.errors.arabicName && (
                  <FormHelperText error sx={categoryStyles.errorText}>
                    {formik.errors.arabicName}
                  </FormHelperText>
                )}
              </FormControl>
            </Box>
          </Grid>

          {/* Buttons */}
          <Grid>
            <Box sx={categoryStyles.buttonsWrapper}>
              <Box sx={{ minWidth: { xs: '100%', sm: '250px' } }} />
              <Stack
                direction="row"
                spacing={2}
                justifyContent="flex-start"
                sx={{ width: '100%' }}
              >
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  sx={{ bgcolor: primary.main }}
                  disabled={
                    mode === 'edit' &&
                    (!formik.dirty || !formik.isValid || loading)
                  }
                >
                  {loading ? 'Saving...' : mode === 'edit' ? 'Update' : 'Save'}
                </Button>
                <Button
                  variant="contained"
                  sx={categoryStyles.cancelButton}
                  onClick={() => {
                    formik.resetForm();
                    onCancel();
                  }}
                >
                  Cancel
                </Button>
              </Stack>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Paper>
  );
};

export default ItemCategoryForm;
